import * as THREE from 'three'

import Debug from './Utils/Debug.js'
import Sizes from './Utils/Sizes.js'
import Time from './Utils/Time.js'
import Camera from './Camera.js'
import Renderer from './Renderer.js'
import World from './World/World.js'
import Resources from './Utils/Resources.js'

import sources from './sources.js'

let instance = null

export default class Experience
{
    constructor(_canvas)
    {
        // Singleton
        if(instance)
        {
            return instance
        }
        instance = this
        
        // Global access
        window.experience = this

        this.scrollProgress = 0
        this.isFirstFrameReady = false
        this.setMessageBridge()

        // Options
        this.canvas = _canvas

        // Setup
        this.debug = new Debug()
        this.sizes = new Sizes()
        this.time = new Time()
        this.scene = new THREE.Scene()
        this.resources = new Resources(sources)
        this.camera = new Camera()
        this.renderer = new Renderer()
        this.world = new World()

        // Resize event
        this.sizes.on('resize', () =>
        {
            this.resize()
        })

        this.needsUpdate = true

        // Time tick event
        this.time.on('tick', () =>
        {
            this.update()
        })
    }

    setMessageBridge()
    {
        window.addEventListener('message', (event) =>
        {
            if(event.data?.type === 'scroll')
            {
                const progress = Number(event.data.progress)

                if(Number.isFinite(progress))
                {
                    this.scrollProgress = Math.max(0, Math.min(1, progress))
                    this.needsUpdate = true
                }
            }

            if(event.data?.type === 'host-ready')
            {
                this.resources?.sendState()

                if(this.isFirstFrameReady)
                    window.parent.postMessage({ type: 'model-ready' }, '*')
            }

            if(event.data?.type === 'mouse-move')
            {
                const x = Number(event.data.x)
                const y = Number(event.data.y)

                if(Number.isFinite(x) && Number.isFinite(y))
                    this.camera.setParallaxTarget(x, y)
            }
        })
    }

    resize()
    {
        this.camera.resize()
        this.renderer.resize()
        this.needsUpdate = true
    }

    update()
    {
        this.world.update()
        this.camera.updateParallax()

        if(!this.needsUpdate)
            return

        this.camera.update()
        this.renderer.update()
        this.needsUpdate = false
    }

    destroy()
    {
        this.sizes.off('resize')
        this.time.off('tick')

        // Traverse the whole scene
        this.scene.traverse((child) =>
        {
            // Test if it's a mesh
            if(child instanceof THREE.Mesh)
            {
                child.geometry.dispose()

                // Loop through the material properties
                for(const key in child.material)
                {
                    const value = child.material[key]

                    // Test if there is a dispose function
                    if(value && typeof value.dispose === 'function')
                    {
                        value.dispose()
                    }
                }
            }
        })

        this.camera.controls.dispose()
        this.renderer.instance.dispose()

        if(this.debug.active)
            this.debug.ui.destroy()
    }
}